import React, { useEffect } from 'react';
import { testimonials } from '../data/testimonials';
import theme from '../config/theme';

interface TestimonialsPageProps {
    user: {
        name: string;
        email: string;
        picture?: string;
    } | null;
    onSignOut: () => void;
    onSignIn: () => void;
}

const TestimonialsPage: React.FC<TestimonialsPageProps> = ({ user, onSignOut, onSignIn }) => {

    // Scroll to top on mount
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div style={{
            minHeight: '100vh',
            background: `linear-gradient(135deg, ${theme.colors.bg} 0%, ${theme.colors.bgAlt} 100%)`,
            paddingTop: '80px' // Space for fixed header
        }}>
            {/* Hero Section */}
            <div style={{
                textAlign: 'center',
                padding: 'clamp(40px, 8vw, 80px) 20px',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Background Decorations */}
                <div style={{
                    position: 'absolute',
                    top: '-20%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '100%',
                    height: '100%',
                    background: 'radial-gradient(circle at center, rgba(99, 102, 241, 0.15) 0%, transparent 70%)',
                    filter: 'blur(60px)',
                    zIndex: 0
                }} />

                <div style={{ position: 'relative', zIndex: 1, maxWidth: '800px', margin: '0 auto' }}>
                    <h2 style={{
                        fontSize: 'clamp(14px, 3vw, 16px)',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        letterSpacing: '2px',
                        color: theme.colors.primary,
                        marginBottom: '16px',
                        display: 'inline-block',
                        padding: '8px 16px',
                        background: 'rgba(99, 102, 241, 0.1)',
                        borderRadius: '30px',
                        backdropFilter: 'blur(4px)'
                    }}>
                        Student Success Stories
                    </h2>

                    <h1 style={{
                        fontSize: 'clamp(2.5rem, 6vw, 4rem)',
                        fontWeight: '800',
                        lineHeight: '1.1',
                        marginBottom: '24px',
                        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        letterSpacing: '-0.02em'
                    }}>
                        Loved by{' '}
                        <span style={{
                            background: 'linear-gradient(135deg, #4F46E5 0%, #EC4899 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}>
                            Students
                        </span>
                    </h1>

                    <p style={{
                        fontSize: 'clamp(1.1rem, 2.5vw, 1.25rem)',
                        color: '#64748b',
                        lineHeight: '1.6',
                        maxWidth: '600px',
                        margin: '0 auto'
                    }}>
                        Discover how SDETPRO has helped students master automation testing and advance their careers.
                    </p>
                </div>
            </div>

            {/* Testimonials Grid (Masonry Layout) */}
            <div style={{
                maxWidth: '1400px',
                margin: '0 auto',
                padding: '0 clamp(16px, 4vw, 32px) 80px',
                columns: '3 350px',
                columnGap: '24px'
            }}>
                {testimonials.map((testimonial, index) => (
                    <div
                        key={testimonial.id}
                        style={{
                            breakInside: 'avoid',
                            marginBottom: '24px',
                            background: 'rgba(255, 255, 255, 0.7)',
                            backdropFilter: 'blur(12px)',
                            borderRadius: '24px',
                            padding: '32px',
                            border: '1px solid rgba(255, 255, 255, 0.5)',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                            cursor: 'default',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-8px)';
                            e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.05)';
                        }}
                    >
                        {/* Quote Icon Background */}
                        <div style={{
                            position: 'absolute',
                            top: '20px',
                            right: '20px',
                            fontSize: '80px',
                            lineHeight: '1',
                            color: 'rgba(99, 102, 241, 0.05)',
                            fontFamily: 'serif',
                            pointerEvents: 'none'
                        }}>
                            "
                        </div>

                        {/* Content */}
                        <p style={{
                            fontSize: '16px',
                            lineHeight: '1.7',
                            color: '#334155',
                            marginBottom: '24px',
                            position: 'relative',
                            zIndex: 1,
                            fontStyle: 'italic'
                        }}>
                            "{testimonial.content}"
                        </p>

                        {/* Author Info */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            borderTop: '1px solid rgba(0, 0, 0, 0.05)',
                            paddingTop: '20px'
                        }}>
                            {/* Avatar Placeholder */}
                            <div style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, ${['#4F46E5', '#EC4899', '#8B5CF6', '#06B6D4'][index % 4]} 0%, ${['#818CF8', '#F472B6', '#A78BFA', '#22D3EE'][index % 4]} 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: '700',
                                fontSize: '18px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                            }}>
                                {testimonial.name.charAt(0)}
                            </div>

                            <div>
                                <h3 style={{
                                    fontSize: '16px',
                                    fontWeight: '700',
                                    color: '#1e293b',
                                    margin: '0 0 4px 0'
                                }}>
                                    {testimonial.name}
                                </h3>
                                <div style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: '#64748b',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    <span style={{
                                        width: '6px',
                                        height: '6px',
                                        borderRadius: '50%',
                                        backgroundColor: '#10b981',
                                        display: 'inline-block'
                                    }} />
                                    {testimonial.course}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TestimonialsPage;
